import { useCallback, useState, useEffect, useRef } from 'react';
import integration from '@sport/integration';
import './App.scss';
import Modal from './components/Modal/Modal';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Sidebar from './components/Sidebar/Sidebar';
import Settings, { themes } from './components/Settings/Settings';
import { useUser } from './components/UserProvider/UserProvider';
import { useLanguageService } from './translations';
import useNavigation, { getUrlLang } from './hooks/useNavigation';
import { prepareUrl } from 'helpers';
import auth from 'services/auth';
import useViewMode from './hooks/useViewMode';
import { isIframeTurkeyHost } from './hostsConfig';

const DEFAULT_THEME = 'brand2';

function App() {
  if (!localStorage.getItem('prefers-color-theme')) {
    localStorage.setItem('prefers-color-theme', DEFAULT_THEME);
  }
  const [currentThemeName, setCurrentThemeName] = useState(localStorage.getItem('prefers-color-theme') || DEFAULT_THEME);
  const themeRef = useRef(currentThemeName);
  themeRef.current = currentThemeName;
  document.body.setAttribute('data-theme-name', currentThemeName);
  const { lang, setLang, langCodes, isRtl, t } = useLanguageService();
  const [currency, setCurrency] = useState();
  const [showSettings, setShowSettings] = useState(false);
  const {
    user,
    setUser,
    isAuthPending,
    setIsAuthPending,
    refreshBalance,
    setRefreshBalance,
    transferBalance,
    setTransferBalance,
  } = useUser();
  const { navigateByPath, clientPath, search } = useNavigation(lang, langCodes);
  const [url, setUrl] = useState(prepareUrl(lang, '/'));
  const iframeUrlLang = getUrlLang(url, langCodes);
  const { isWideView } = useViewMode();
  const { helpers } = integration;

  useEffect(() => {
    integration.init({
      languages: langCodes,
      prefix: 'sportsbook',
    });
  }, [langCodes]);

  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    helpers.logout();
    await auth.logout();
    setUser(null);
  }, [setUser, helpers]);

  const login = useCallback(() => {
    navigateByPath('/login');
  }, [navigateByPath]);

  const fetchTransferBalance = useCallback(async (payload, cb) => {
    await auth.transferBalance(payload, cb);
  }, []);

  useEffect(() => {
    if (!url && clientPath) {
      const urlLang = getUrlLang(clientPath, langCodes);
      urlLang && urlLang !== lang && setLang(urlLang);
    }
  }, [clientPath, lang, langCodes, setLang, url]);

  useEffect(() => {
    if (iframeUrlLang !== lang) {
      setLang(lang);
      setUrl(prepareUrl(lang, '/'));
    }
  }, [iframeUrlLang, lang, setLang]);

  useEffect(() => {
    integration.mount('frame', url);
  }, [url]);

  const checkUser = useCallback(async () => {
    if (localStorage.getItem('token')) {
      const user = await auth.getUser(localStorage.getItem('token'));
      setUser(user);
      //integration.emit({ balance: user?.balance, currency: user?.currency });
    }
  }, [setUser]);

  useEffect(() => {
    if (user?.jwt) {
      setUrl((url) => {
        const data = new URL(url);
        const search = new URLSearchParams({
          jwt: user.jwt.jwt,
        });
        return data.origin + data.pathname + '?' + search.toString();
      });
      setIsAuthPending(false);
    }
  }, [setIsAuthPending, user?.jwt, lang, search, setUrl]);

  useEffect(() => {
    checkUser();
  }, [refreshBalance, checkUser]);

  useEffect(() => {
    const on = integration.on;
    const unsubscriptions = [];
    unsubscriptions.push(on('navigation', (data) => {
      const {previousPath, path} = data;
      if (previousPath === '/mini-iframe') { // navigate from mini-iframe on outcome click
        integration.emit({ path }, 'iframe-navigation');
      }
    }));
    unsubscriptions.push(on('login.click', login));
    unsubscriptions.push(
      on('logout', () => {
        localStorage.removeItem('token');
        setUser(null);
      }),
    );
    unsubscriptions.push(
      on('user.balance', () => {
        setRefreshBalance((v) => v + 1);
      }),
      on('openLink', ({url}) => {
        window.location.href = url;
      }),
    );
    unsubscriptions.push(on('user.balance_transfer', setTransferBalance));
    unsubscriptions.push(on('sdkInit', () => {
      helpers.emitTheme(themes.find(({ name }) => name === themeRef.current) || themeRef.current);
    }));
    unsubscriptions.push(
      on('user.multiCurrencyBalance', ({ data }) => {
        data.selectedCurrency && setCurrency(data.selectedCurrency);
      }),
    );
    return () => {
      unsubscriptions.forEach(({ off }) => {
        off();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, setTransferBalance, setRefreshBalance, setUser]);

  useEffect(() => {
    if (transferBalance.value) {
      fetchTransferBalance(transferBalance.value, (val) => {
        integration.emit({ balanceTransferSuccess: val });
        checkUser();
      });
    }
  }, [transferBalance, fetchTransferBalance, checkUser]);

  const changeCurrency = (currency) => {
    integration.helpers.updateCurrency(currency);
    setCurrency(currency);
  };

  const toggleSettings = () => setShowSettings(prev => !prev);

  const refreshCallback = () => {
    window.scrollTo(0, 0);
    navigateByPath('/');
    window.location.reload();
  };

  if (!url || isAuthPending) {
    return null;
  }

  return (
    <div
      className={`App ${isIframeTurkeyHost ? 'turkey' : ''}`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <Modal
        title={t('Brand customization')}
        close={toggleSettings}
        openModal={showSettings}
      >
        <Settings updateLang={setLang} updateTheme={setCurrentThemeName} />
      </Modal>
      <Header
        currency={currency}
        user={user}
        login={login}
        logout={logout}
        refresh={refreshCallback}
        updateLang={setLang}
        changeCurrency={changeCurrency}
        settingsClick={toggleSettings}
        currentTheme={currentThemeName}
      />
      <main>
        {isIframeTurkeyHost && isWideView && <Sidebar />}
        <div id="frame"></div>
      </main>
      <Footer
        refresh={refreshCallback}
        currentTheme={currentThemeName}
      />
    </div>
  );
}

export default App;
